import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Layout from './layout'
import styles from '../component-styles/website-picker.module.scss'
import Button from './button'
import {
  getAssociatedAccounts,
  AssociatedAccount,
  AssociatedProperty,
  AssociatedView,
  addAccount,
  addFirstEnterpriseAccount,
} from '../api-client/settings-client'
import { getSelectedAccountAsync } from '../store/actions'

import { connectFirstAccount } from '../api-client/setup-client'
import SelectBoxTypeahead from './select-box-typeahead'
import { getItemByKeyValue } from '../helpers'
import { Label } from './input'
import { FormRow, LabelSlot, FieldSlot } from './row'
import { ModalBody, ModalFooter } from './modal'
import ConfirmationButtons from './confirmation-buttons'
import { GraphQLState } from '../store/graphql-reducer'
import { SiteState } from '../store/site-reducer'
import { Preloader } from './loader'

export default function WebsitePicker({
  onClick,
  modal,
  onToggle,
}: {
  onClick?: () => void
  modal?: boolean
  onToggle?: () => void
}) {
  const dispatch = useDispatch()

  const [accountData, setAccountData] = useState<AssociatedAccount[]>([])
  const [account, setAccount] = useState<AssociatedAccount | null>(null)
  const [property, setProperty] = useState<AssociatedProperty | null>(null)
  const [view, setView] = useState<AssociatedView | null>(null)
  const [submitError, setSubmitError] = useState('')
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const {
    graphql: { currentUser, currentAccount },
    site: { selected },
  }: {
    graphql: GraphQLState
    site: SiteState
  } = useSelector((s: any): any => s)

  const accountList = [...(currentUser?.userAccountProfiles || [])]

  const oauth = useSelector((state: any): any => state.oauth)
  const { credentials, loading } = oauth

  useEffect(() => {
    dispatch(getSelectedAccountAsync())
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!loading && (credentials === '' || accountData.length > 0)) return
    ;(async () => {
      const accounts = await getAssociatedAccounts({ credentials })
      if (accounts?.length > 0) {
        setAccountData(accounts)
      }
    })()
    // eslint-disable-next-line
  }, [accountData, setAccountData, credentials])

  const mapAccount = (acc: AssociatedAccount) => ({
    gaAccountName: acc.name,
    gaAccountID: acc.id,
  })

  const mapProperty = (acc: AssociatedProperty) => ({
    gaPropertyName: acc.name,
    gaPropertyID: acc.id,
  })

  const mapView = (acc: AssociatedView) => ({
    gaViewName: acc.name,
    gaViewID: acc.id,
  })

  const handleSubmit = async () => {
    if (!account || !property || !view) return

    setSubmitError('')

    const isFirstEnterpriseAccountToConnect = selected && selected.blankAccount

    const isFirstAccountToConnect =
      accountList.length < 1 ||
      (accountList.length === 1 &&
        currentAccount &&
        currentAccount.blankAccount)
    let success
    setLoadingSubmit(true)
    if (isFirstEnterpriseAccountToConnect) {
      success = await addFirstEnterpriseAccount({
        ...mapAccount(account),
        ...mapProperty(property),
        ...mapView(view),
        credentials,
      })
    } else if (isFirstAccountToConnect) {
      success = await connectFirstAccount({
        ...mapAccount(account),
        ...mapProperty(property),
        ...mapView(view),
        credentials,
      })
    } else {
      success = await addAccount({
        ...mapAccount(account),
        ...mapProperty(property),
        ...mapView(view),
        credentials,
      })
    }
    setLoadingSubmit(false)
    if (!success) {
      setSubmitError('Sorry, we could not add this account. Please try again.')
      return
    }

    if (onClick) {
      onClick()
      return
    }

    window.location.assign('/welcome')
  }

  const Content = () => {
    return (
      <>
        <FormRow last>
          <LabelSlot column>
            <Label id="account">Account</Label>
          </LabelSlot>
          <FieldSlot column>
            <SelectBoxTypeahead
              id="account"
              key={`account${accountData.length}`}
              disabled={accountData.length === 0}
              placeholder="Choose your account"
              keyName="name"
              keyValue="id"
              onInputChange={(val: string): void => {
                const acc = getItemByKeyValue(accountData, 'id', val)
                setAccount(acc === -1 ? null : acc)
                setProperty(null)
                setView(null)
              }}
              items={accountData}
            />
          </FieldSlot>
        </FormRow>
        <FormRow last>
          <LabelSlot column>
            <Label id="property">Property</Label>
          </LabelSlot>
          <FieldSlot column>
            <SelectBoxTypeahead
              key={`property-${JSON.stringify(account)}`}
              id="property"
              disabled={!account}
              placeholder="Choose your property"
              keyName="name"
              keyValue="id"
              onInputChange={(val: string): void => {
                const prop = account
                  ? getItemByKeyValue(account.webProperties, 'id', val)
                  : null
                setProperty(prop === -1 ? null : prop)
                setView(null)
              }}
              items={account?.webProperties || []}
            />
          </FieldSlot>
        </FormRow>
        <FormRow last>
          <LabelSlot column>
            <Label id="view">View</Label>
          </LabelSlot>
          <FieldSlot column>
            <SelectBoxTypeahead
              key={`view-${JSON.stringify(property)}`}
              id="view"
              disabled={!property}
              placeholder="Choose your view"
              keyName="name"
              keyValue="id"
              onInputChange={(val: string): void => {
                const siteView = property
                  ? getItemByKeyValue(property.views, 'id', val)
                  : null
                setView(siteView === -1 ? null : siteView)
              }}
              items={property?.views || []}
            />
          </FieldSlot>
        </FormRow>
      </>
    )
  }

  if (modal) {
    return (
      <div>
        <ModalBody>
          {loadingSubmit ? (
            <div className={styles.preloader}>
              <Preloader style={{ width: 75, height: 60 }} />
            </div>
          ) : (
            <>
              {Content()}
              {submitError && <p className={styles.error}>{submitError}</p>}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <ConfirmationButtons
            disabled={!(account && property && view)}
            type="simple"
            onYes={(): void => {
              handleSubmit()
            }}
            onNo={(): void => {
              if (onToggle) {
                onToggle()
              }
            }}
            yesLabel="Connect website"
            noLabel="Go back"
          />
        </ModalFooter>
      </div>
    )
  }

  return (
    <Layout width={544}>
      <div className={styles.container}>
        <h1 className={styles.title}>Connect your website</h1>
        <p>Select the website you would like to improve.</p>
        <Layout width={470} style={{ marginTop: 8 }}>
          {loadingSubmit ? (
            <div className={styles.preloader}>
              <Preloader style={{ width: 75, height: 60 }} />
            </div>
          ) : (
            <>
              {Content()}
              {submitError && <p className={styles.error}>{submitError}</p>}
              <Button
                disabled={!(account && property && view)}
                type="squareRed"
                className={styles.submit}
                onClick={handleSubmit}
              >
                Connect website
              </Button>
            </>
          )}
        </Layout>
      </div>
    </Layout>
  )
}
